import React, { useState, useEffect, KeyboardEvent, useRef } from 'react';
import { useUser } from '../hooks';
import { Toast, Button, Icon, Header, Placeholder } from 'react-frontier';
import { Dropdown } from 'semantic-ui-react';
import { IssueComment, UserClass } from '../SiteClasses';
import { randomRange } from '../SiteUtil';
import { firstBy } from 'thenby';
import moment from 'moment';
import API from '../API';
import classNames from 'classnames';

var PAGE_SIZE = 1000;
var MENTION_REGEX = /@([a-zA-Z0-9_\u00C0-\u017F]+)/g;

interface IssueCommentsProps{
	issueId: number,
}

var IssueComments = (props: IssueCommentsProps)=>{
	var { user } = useUser();
	var [comments, setComments] = useState<(IssueComment & { sending?: boolean, local_id?: number })[]>(null);
	var [newComment, setNewComment] = useState<string>("");
	var [inputEmpty, setInputEmpty] = useState<boolean>(true);
	var [showMentions, setShowMentions] = useState<boolean>(false);
	var [mentionSearch, setMentionSearch] = useState<string>("");
	var [mentionUsers, setMentionUsers] = useState<UserClass[]>([]);
	var [selectedMentionIndex, setSelectedMentionIndex] = useState<number>(0);
	var [cursorPosition, setCursorPosition] = useState<number>(0);
	var [mentionedUsersMap, setMentionedUsersMap] = useState<Map<string, number>>(new Map());
	var inputRef = useRef<HTMLDivElement>(null);
	var commentRef = useRef<typeof comments>(null);
	commentRef.current = comments;

	useEffect(()=>{
		if(props.issueId) getComments();
	}, [props.issueId]);

	useEffect(()=>{
		if(showMentions){
			searchMentionUsers();
		}else{
			setMentionUsers([]);
			setSelectedMentionIndex(0);
		}
	}, [showMentions, mentionSearch]);

	var searchMentionUsers = ()=>{
		API.searchUsers(mentionSearch, 0, 10).then(res=>{
			if(!res.error) setMentionUsers(res.data);
		}).catch(()=>{
			Toast.error('Hubo un error obteniendo los usuarios (LCL-1)');
		});
	};

	var getComments = ()=>{
		API.getIssueComments(props.issueId, PAGE_SIZE, 0).then(res=>{
			if(res.error) return Toast.error(res.message);
			setComments(res.data);
		}).catch(()=>{
			Toast.error('Hubo un error cargando los comentarios (LCL-1)');
		});
	};

	var sendComment = ()=>{
		var trimmedComment = newComment.replace(/&nbsp;/gi, ' ').trim();
		if(trimmedComment.length < 3){
			return Toast.error("El comentario debe de ser de mínimo 3 caracteres");
		}
		
		var mentioned_users = Array.from(mentionedUsersMap.values());
		var temp_comment : typeof comments[0] = {
			comment_id: null,
			issue_id: props.issueId,
			content: trimmedComment,
			date_created: moment().unix(),
			user_creator_name: user.name,
			user_id: user.user_id,
			local_id: randomRange(0, 25565556),
			sending: true,
		};

		setComments([...commentRef.current, temp_comment].sort(firstBy('date_created', 'desc')));
		if(inputRef.current) inputRef.current.textContent = '';
		setNewComment("");
		setMentionedUsersMap(new Map());
		setInputEmpty(true);

		API.createIssueComment(props.issueId, temp_comment.content, mentioned_users).then(res=>{
			var nc = commentRef.current.filter(a=>a.local_id !== temp_comment.local_id);
			if(res.error){
				setComments(nc);
				return Toast.error(res.message);
			}
			nc.push({
				...temp_comment,
				sending: false,
				local_id: null,
				comment_id: res.data.comment_id
			});
			setComments(nc.sort(firstBy('date_created', 'desc')));
		}).catch(()=>{
			setComments(commentRef.current.filter(a=>a.local_id !== temp_comment.local_id));
			Toast.error('Hubo un error inesperado agregando el comentario (LCL-1)');
		});
	};

	var deleteComment = (comment: typeof comments[0])=>{
		API.deleteIssueComment(comment.comment_id).then(res=>{
			if(res.error) return Toast.error(res.message);
			Toast.success("Comentario eliminado!");
			setComments(commentRef.current.filter(a=>a.comment_id !== comment.comment_id));
		}).catch(()=>{
			Toast.error('Hubo un error eliminando el comentario (LCL-1)');
		});
	};

	var onMentionKeyDown = (ev: KeyboardEvent)=>{
		switch(ev.key){
			case 'ArrowDown':
				ev.preventDefault();
				setSelectedMentionIndex(prev=>Math.min(prev + 1, mentionUsers.length - 1));
				break;
			case 'ArrowUp':
				ev.preventDefault();
				setSelectedMentionIndex(prev=>Math.max(prev - 1, 0));
				break;
			case 'Enter':
			case 'Tab':
				ev.preventDefault();
				if(mentionUsers[selectedMentionIndex]){
					insertMention(mentionUsers[selectedMentionIndex]);
				}
				break;
			case 'Escape':
				setShowMentions(false);
				setMentionSearch("");
				break;
		}
	};

	var onInputKeyDown = (ev: KeyboardEvent)=>{
		if(showMentions){
			onMentionKeyDown(ev);
		}else if(ev.key === 'Enter'){
			if(ev.ctrlKey){
				ev.preventDefault();
				var selection = window.getSelection();
				if(selection && selection.rangeCount > 0){
					var range = selection.getRangeAt(0);
					range.deleteContents();
					
					var br = document.createElement('br');
					range.insertNode(br);
					
					var textNode = document.createTextNode('');
					br.parentNode?.insertBefore(textNode, br.nextSibling);
					
					range.setStart(textNode, 0);
					range.collapse(true);
					selection.removeAllRanges();
					selection.addRange(range);
					
					if(inputRef.current){
						var event = new Event('input', { bubbles: true });
						inputRef.current.dispatchEvent(event);
					}
				}
			}else{
				ev.preventDefault();
				sendComment();
			}
		}
	};

	var onInput = (ev: React.FormEvent<HTMLDivElement>)=>{
		var htmlContent = ev.currentTarget.innerHTML;
		var text = htmlContent
			.replace(/&nbsp;/gi, ' ')
			.replace(/<br\s*\/?>/gi, '\n')
			.replace(/<[^>]*>/g, '');
		setInputEmpty(text.trim().length === 0);
		setNewComment(text);
		
		var cursorPos = getCursorPosition(ev.currentTarget);
		updateInputWithMentions(ev.currentTarget, htmlContent, cursorPos);
		
		var textBeforeCursor = text.substring(0, cursorPos);
		var lastAtIndex = textBeforeCursor.lastIndexOf('@');
		
		if(lastAtIndex !== -1){
			var textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
			if(!textAfterAt.includes(' ')){
				setShowMentions(true);
				setMentionSearch(textAfterAt);
				setSelectedMentionIndex(0);
				setCursorPosition(lastAtIndex);
				return;
			}
		}
		
		setShowMentions(false);
		setMentionSearch("");
	};

	var getCursorPosition = (element: HTMLDivElement): number=>{
		var selection = window.getSelection();
		if(!selection?.rangeCount) return 0;
		
		var range = selection.getRangeAt(0);
		var preCaretRange = range.cloneRange();
		preCaretRange.selectNodeContents(element);
		preCaretRange.setEnd(range.endContainer, range.endOffset);
		return preCaretRange.toString().length;
	};

	var setCaret = (node: Node, range: Range, cursorPos: number, currentPos: { val: number }): boolean=>{
		if(node.nodeType === Node.TEXT_NODE){
			var nodeLength = node.textContent?.length || 0;
			if(currentPos.val + nodeLength >= cursorPos){
				range.setStart(node, cursorPos - currentPos.val);
				range.collapse(true);
				return true;
			}
			currentPos.val += nodeLength;
		}else if(node.nodeType === Node.ELEMENT_NODE){
			for(var child of Array.from(node.childNodes)){
				if(setCaret(child, range, cursorPos, currentPos)) return true;
			}
		}
		return false;
	};

	var updateInputWithMentions = (element: HTMLDivElement, htmlContent: string, cursorPos: number)=>{
		var htmlWithMarkers = htmlContent.replace(/<br\s*\/?>/gi, '___BR___');
		var textWithMarkers = htmlWithMarkers.replace(/<[^>]*>/g, '');
		
		var html = '';
		var lastIndex = 0;
		var match;
		var regex = new RegExp(MENTION_REGEX);
		
		while((match = regex.exec(textWithMarkers)) !== null){
			html += textWithMarkers.substring(lastIndex, match.index);
			html += `<span class="input-mention">${match[0]}</span>`;
			lastIndex = match.index + match[0].length;
		}
		html += textWithMarkers.substring(lastIndex);
		
		html = html.replace(/___BR___/g, '<br>');
		
		if(html !== element.innerHTML){
			element.innerHTML = html || '';
			var selection = window.getSelection();
			var range = document.createRange();
			if(element.childNodes.length > 0){
				setCaret(element, range, cursorPos, { val: 0 });
				selection?.removeAllRanges();
				selection?.addRange(range);
			}
		}
	};

	var insertMention = (mentionUser: UserClass)=>{
		var firstName = mentionUser.first_name || '';
		var lastName = mentionUser.last_name || '';
		var userName = (firstName + lastName).replace(/\s+/g, '') || mentionUser.name?.replace(/\s+/g, '') || mentionUser.username;
		var currentText = (newComment || '').replace(/&nbsp;/gi, ' ');
		var beforeMention = currentText.substring(0, cursorPosition);
		var afterMention = currentText.substring(cursorPosition + mentionSearch.length + 1);
		var newText = `${beforeMention}@${userName} ${afterMention}`;
		
		setMentionedUsersMap(prev=>new Map(prev).set(userName, mentionUser.user_id));
		
		if(inputRef.current){
			inputRef.current.textContent = newText;
			setNewComment(newText);
			
			var newPos = beforeMention.length + userName.length + 2;
			updateInputWithMentions(inputRef.current, newText, newPos);
			inputRef.current.focus();
		}
		
		setShowMentions(false);
		setMentionSearch("");
		setInputEmpty(false);
	};

	var renderCommentContent = (content: string)=>{
		if(!content) return '';
		var cleanContent = content.replace(/&nbsp;/gi, ' ');
		var parts: JSX.Element[] = [];
		var lastIndex = 0;
		var match;
		var regex = new RegExp(MENTION_REGEX);
		
		while((match = regex.exec(cleanContent)) !== null){
			if(match.index > lastIndex){
				var textSegment = cleanContent.substring(lastIndex, match.index);
				parts.push(...renderTextWithLineBreaks(textSegment, lastIndex));
			}
			parts.push(<span key={`mention-${match.index}`} className="mention">{match[0]}</span>);
			lastIndex = match.index + match[0].length;
		}
		
		if(lastIndex < cleanContent.length){
			var textSegment = cleanContent.substring(lastIndex);
			parts.push(...renderTextWithLineBreaks(textSegment, lastIndex));
		}
		
		return parts.length > 0 ? parts : cleanContent;
	};

	var renderTextWithLineBreaks = (text: string, baseKey: number): JSX.Element[]=>{
		var lines = text.split('\n');
		var elements: JSX.Element[] = [];
		
		lines.forEach((line, index)=>{
			if(index > 0){
				elements.push(<br key={`br-${baseKey}-${index}`} />);
			}
			if(line.length > 0){
				elements.push(<span key={`text-${baseKey}-${index}`}>{line}</span>);
			}
		});
		
		return elements;
	};

	if(!props.issueId) return (
		<div className="ar comments-container empty">
			<div className="empty">
				<Header text='Sin comentarios' subtext='Crea el ticket para habilitar los comentarios' iconName='message' />
			</div>
		</div>
	);

	return <div className={classNames('ar comments-container', {
		empty: comments && comments.length==0
	})}>
		<div className="textbox">
			<div ref={inputRef} className={classNames('input', { empty: inputEmpty })} onInput={onInput} contentEditable onKeyDown={onInputKeyDown} />
			{showMentions && mentionUsers.length > 0 && (
				<div className="mentions-dropdown" onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); }}>
					{mentionUsers.map((mentionUser, index)=>(
						<div 
							key={mentionUser.user_id}
							className={classNames('mention-item', { selected: index === selectedMentionIndex })}
							onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); }}
							onClick={()=>insertMention(mentionUser)}
							onMouseEnter={()=>setSelectedMentionIndex(index)}
						>
							<Icon name='user' />
							<span className="name">{mentionUser.name || `${mentionUser.first_name || ''} ${mentionUser.last_name || ''}`.trim()}</span>
							<span className="email">{mentionUser.email}</span>
						</div>
					))}
				</div>
			)}
			<div className="right">
				<Button iconName='arrow-right' color='blue' icon onClick={sendComment} />
			</div>
		</div>
		{!comments ? (
			<div className="comments">
				<div className="comment placeholder">
					<div className="content">
						<div className="message">
							<div className="text"><Placeholder /></div>
						</div>
					</div>
				</div>
				<div className="comment placeholder sent">
					<div className="content">
						<div className="message">
							<div className="text"><Placeholder /></div>
						</div>
					</div>
				</div>
			</div>
		) : comments.length==0 ? (
			<div className="empty">
				<Header text={'Sin comentarios'} subtext={'Esta sección se encuentra vacía'} iconName='message' />
			</div>
		) : (
			<div className="comments">
				{comments.map((a)=>{
					return (
						<div key={`comment-${a.comment_id || a.local_id}`} className={classNames("comment", {
							sent: a.user_id===user.user_id,
							sending: a.sending,
						})}>
							{a.user_id!==user.user_id && (
								<div className="sender">
									{a.user_creator_name}
									<div className="date">{moment.unix(a.date_created).format('DD/MMM HH:mm')}</div>
								</div>
							)}
							<div className="content">
								<div className="message">
									{a.sending ? (
										<div className="fr inline loading"></div>
									) : a.user_id === user.user_id && (
										<Dropdown direction='left' additionPosition='top' trigger={(
											<Icon name='ellipsis-vertical' />
										)} icon={null}>
											<Dropdown.Menu direction='left'>
												<Dropdown.Item 
													text={'Eliminar'} 
													onClick={()=>deleteComment(a)}
												/>
											</Dropdown.Menu>
										</Dropdown>
									)}
									<div className="text">
										{renderCommentContent(a.content)}
									</div>
								</div>
								{a.user_id===user.user_id && (
									<div className="date">{moment.unix(a.date_created).format('DD/MMM HH:mm')}</div>
								)}
							</div>
						</div>
					)
				})}
			</div>
		)}
	</div>
}

export default IssueComments;
